<template>
  <div class="app-multiselect" :class="{ small, 'hide-selected': hideSelected, 'invalid': currentlyInvalid, 'is-single': singleSelect, 'is-searchable': searchable }">
    <app-loading v-show="loading" />
    <multiselect 
      label="label"
      track-by="id"
      :placeholder="innerPlaceholder"
      select-label=""
      deselect-label=""
      :options="innerOptions"
      :value="innerValue" 
      @input="onInput"
      :multiple="!singleSelect"
      :disabled="disabled"
      :searchable="searchable"
      :allow-empty="allowEmpty"
      :close-on-select="closeOnSelect"
      :open-direction="openDirection"
      @open="onOpen"
      @close="onClose"
      @search-change="onSearchChange"
      :class="{ 'force-single-line': forceSingleLine }"
    >
      <template #noResult>
        {{ $t("nothing-found") }}
      </template>
      <!-- Pass scoped slots through -->
      <template v-for="(_, name) in $scopedSlots" v-slot:[name]="slotData">
        <slot :name="name" v-bind="slotData" />
      </template>
      <!-- Pass slots through -->
      <template v-for="(_, slot) in $slots" :slot="slot">
        <slot :name="slot" />
      </template>
    </multiselect>
    <div v-show="readonly" class="app-multiselect-readonly"></div>
  </div>
</template>
<script lang="ts">
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import Multiselect from "vue-multiselect";
import { MultiselectOption, MultiselectOptionId, NullableMultiselectOption } from "./types";
import { CatchError } from "../../services/helper/catch-helper";
import AppLoading from "../app-loading/app-loading.vue";

@Component({
  name: "app-multiselect",
  components: {
    Multiselect,
    AppLoading,
  }
})
export default class AppMultiselect extends Vue {
  @Prop({ default: null }) value!: MultiselectOptionId | MultiselectOptionId[] | null;
  @Prop({ default: [] }) options!: MultiselectOption[];
  @Prop({ default: false }) disabled!: boolean;
  @Prop({ default: false }) readonly!: boolean;
  @Prop({ default: false }) singleSelect!: boolean;
  @Prop({ default: false }) allowEmpty!: boolean;
  @Prop({ default: false }) small!: boolean
  @Prop({ default: false }) searchable!: boolean;
  @Prop({ default: true }) hideSelected!: boolean;
  @Prop({ default: true }) closeOnSelect!: boolean;
  @Prop({ default: "" }) placeholder!: string;
  @Prop({ default: "" }) searchPlaceholder!: string;
  @Prop({ default: false }) required!: boolean;
  @Prop({ default: false }) loading!: boolean;
  @Prop({ default: true }) forceSingleLine!: boolean;
  @Prop({ default: "" }) openDirection!: string;

  innerValue: NullableMultiselectOption | NullableMultiselectOption[] | null = null;
  innerOptions: NullableMultiselectOption[] = [];
  innerPlaceholder: string = "";
  currentlyInvalid = true;

  created() {
    this.innerPlaceholder = this.placeholder;
    this.syncInnerOptions();

    this.onValueChanged();
  }

  @Watch('value') onValueChanged() {
    this.checkIfIsCurrentlyValid();

    const filteredOptions = this.value !== null && 
      this.options.filter(option => 
        Array.isArray(this.value) ? this.value!.includes(option.id) : this.value === option.id) || [];

    if (filteredOptions.length > 0) {
      this.innerValue = this.singleSelect ? filteredOptions[0] : filteredOptions;
    } else {
      this.innerValue = null;
    }
  }

  @Watch("placeholder") onPlaceholderChanged() {
    this.innerPlaceholder = this.placeholder;
  }

  @Watch('options') onOptionsChanged() {
    this.syncInnerOptions();

    this.onValueChanged();
  }

  onInput(value: MultiselectOption | MultiselectOption[]) {
    this.innerValue = value;

    if (this.allowEmpty && this.singleSelect) {
      this.removeEmptyOption();
    }

    this.emitValue();
  }

  @CatchError()
  onOpen() {
    if (this.searchable) {
      this.innerPlaceholder = this.searchPlaceholder || this.$t("search").toString();
    }

    this.$emit("open");
  }

  @CatchError()
  onClose() {
    if (this.searchable) {
      this.innerPlaceholder = this.placeholder;
    }
  }

  onSearchChange(query: string) {
    this.$emit("search-change", query);
  }

  private syncInnerOptions() {
    this.innerOptions = [...this.options];
    if (this.allowEmpty && this.singleSelect) {
      this.addEmptyOption();
    }
  }
  
  private emitValue() {
    if (Array.isArray(this.innerValue)) {
      if (this.singleSelect) {
        const selectedId = this.innerValue.find(option => option.id !== null)?.id ?? null;
        this.$emit("input", selectedId);
        this.$emit("change", selectedId);
        return;
      }

      const selectedIds = this.innerValue
        .map(option => option.id)
        .filter(id => id !== null) as MultiselectOptionId[];
      this.$emit("input", selectedIds);
      this.$emit("change", selectedIds);
      return;
    }

    const selectedId = this.innerValue?.id ?? null;
    this.$emit("input", selectedId);
    this.$emit("change", selectedId);
  }

  private addEmptyOption() {
    const emptyOption = this.innerOptions.find(o => o.id === null);
    if (!emptyOption) {
      this.innerOptions.unshift({
        id: null,
        label: "",
      })
    }
  }

  private removeEmptyOption() {
    if (Array.isArray(this.innerValue)) {
      const emptyOptionIndex = this.innerValue.findIndex(val => val.id === null);
      if (emptyOptionIndex !== -1) {
        this.innerValue.splice(emptyOptionIndex, 1);
      }
    }
  }

  private checkIfIsCurrentlyValid() {
    if (!this.required || this.allowEmpty) {
      this.currentlyInvalid = false;
    } else {
      this.currentlyInvalid = !this.value;
    }
  }
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style lang="scss">
@import "@/scss/_colors.scss";

.app-multiselect {
  position: relative;

  .multiselect {
    min-height: 38px;
    color: var(--default-text-color);

    &.force-single-line .multiselect__single {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  .multiselect__select {
    min-height: 38px;
  }
  .multiselect__tags {
    min-height: 38px;
    background-color: var(--input-bg-color);
  }
  .multiselect__single, .multiselect__input {
    background-color: var(--input-bg-color);
  }
  .multiselect__option {
    color: var(--default-text-color);

    &--highlight {
      background: var(--primary-bg-highlight-color);
      color: var(--primary-text-color);
    }
    &--selected:not(.multiselect__option--highlight) {
      background: var(--primary-bg-color);
      color: var(--primary-text-color);
    }
  }
  &.hide-selected {
    .multiselect__option--selected:after {
      content: '';
    }
  }

  // make empty/invalid "required" inputs and selects red
  &.invalid .multiselect__tags {
    border-color: var(--danger-text-color);
  }

  // Allow multiselect options list to overflow the size of the input control
  // see https://github.com/shentao/vue-multiselect/issues/832
  .multiselect .multiselect__content-wrapper {
    min-width: 100%;
    width: auto;
    border: 1px solid var(--input-border-color);
    border-radius: 0;
    background-color: var(--input-bg-color);
  }
  .multiselect--active.multiselect--above .multiselect__tags {
    border-top: none;
  }
  .multiselect--active:not(.multiselect--above) .multiselect__tags {
    border-bottom: none;
  }

  .multiselect__placeholder {
    color: var(--input-placeholder-color);
    padding-top: 0;
    margin-bottom: 0;
  }
  .multiselect__input::placeholder {
    color: var(--input-placeholder-color);
  }
  .multiselect__input {
    color: var(--default-text-color);
  }

  .multiselect--disabled .multiselect__current, .multiselect--disabled .multiselect__select {
    background: transparent;
  }

  &.small {
    .multiselect__input {
      font-size: 1em;
      padding-right: 0;
      padding-left: 0;
    }

    .multiselect__option {
      font-size: .8em;
      padding: 5px 12px;
      min-height: 24px;
    }

    .multiselect {
      min-height: 31px;
      max-height: 31px;
    }

    // app-select defaults forceSingleLine to false, so :not(.force-single-line) alone
    // would also grow single-select placeholders. Limit growth to multi-selects, and to
    // searchable selects while open so the search field is not clipped.
    &:not(.is-single) .multiselect:not(.force-single-line),
    &.is-searchable .multiselect--active {
      max-height: none;

      .multiselect__tags {
        max-height: none;
        white-space: normal;
      }
    }

    // The pills of a growing multi-select sit in rows of their own height: as inline blocks they get
    // the gap below the text baseline of the line box, which pushes them above the center.
    &:not(.is-single) .multiselect:not(.force-single-line) {
      .multiselect__tags {
        padding-top: 3.5px;
        padding-bottom: 3.5px;
      }
      .multiselect__tags-wrap {
        display: flex;
        flex-wrap: wrap;
        row-gap: 3px;
      }
      .multiselect__tag {
        margin-bottom: 0;
      }
    }

    &.is-searchable .multiselect--active .multiselect__input {
      display: block;
      width: 100% !important;
      margin-bottom: 0;
    }
    .multiselect__select {
      min-height: 31px;
      max-height: 31px;
      top: 1px;
      padding-right: 0;
    }
    .multiselect__tags {
      min-height: 31px;
      max-height: 31px;
      padding-top: 4px;
      padding-left: 10px;
      white-space: nowrap;

      .multiselect__single {
        margin-bottom: 0;
        padding-left: 0;
        font-size: 0.875rem;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }

  .multiselect__tags {
    border-radius: 0;
    border-color: var(--input-border-color);

    .multiselect__tag {
      border-radius: 10px;
      background: var(--primary-bg-highlight-color);
      
      &-icon {
        border-radius: 0;

        &:hover {
          background: var(--primary-bg-highlight-color);
        }
        &:hover:after {
          color: var(--muted-text-color);
        }
        &:after {
          color: var(--default-text-color);
        }
      }
    }
  }

  &-readonly {
    position: absolute;
    opacity: 0.1;
    background-color: var(--default-text-color);
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }
}
</style>
